import React from 'react';
import CommonHeader from './CommonHeader';
import CommonFooter from './CommonFooter';
import './LegalPages.css';
import config from '../config';

const PrivacyPolicy = ({ onNavigate, onLogin, onSignup }) => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="legal-page">
      <CommonHeader
        activePage="privacy"
        onNavigate={onNavigate}
        onLogin={onLogin}
        onSignup={onSignup}
      />

      <div className="legal-container">
        <div className="legal-content">
          <div className="legal-header">
            <h1>Privacy Policy</h1>
            <p className="last-updated">Last Updated: January 1, {currentYear}</p>
          </div>

          <div className="legal-body">
            <section>
              <h2>1. Introduction</h2>
              <p>Welcome to {config.APP_NAME} ("we," "our," or "us"). We are committed to protecting your privacy and ensuring you have a positive experience when using our form building and PDF generation platform. This Privacy Policy describes how we collect, use, and protect your personal information.</p>
              <p>By using {config.APP_NAME}, you agree to the collection and use of information in accordance with this policy. We will not use or share your information with anyone except as described in this Privacy Policy.</p>
            </section>

            <section>
              <h2>2. Information We Collect</h2>
              <p>We collect several types of information to provide and improve our service:</p>
              
              <h3>2.1 Personal Information</h3>
              <ul>
                <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and password.</li>
                <li><strong>Profile Information:</strong> You may optionally provide additional profile information such as company name, phone number, and billing address.</li>
                <li><strong>Payment Information:</strong> When you upgrade to a paid plan, we collect payment details through our secure payment processors. We do not store full credit card information on our servers.</li>
              </ul>

              <h3>2.2 Usage Information</h3>
              <ul>
                <li><strong>Form Data:</strong> The forms you create, responses you collect, and PDFs you generate.</li>
                <li><strong>Images:</strong> Images uploaded to forms are stored temporarily for PDF generation only.</li>
                <li><strong>Log Data:</strong> We collect information about your interactions with our platform, including IP address, browser type, pages visited, time and date of access.</li>
              </ul>

              <h3>2.3 Cookies and Tracking Technologies</h3>
              <p>We use cookies and similar tracking technologies to track activity on our platform and hold certain information. Cookies are files with small amount of data which may include an anonymous unique identifier.</p>
            </section>

            <section>
              <h2>3. How We Use Your Information</h2>
              <p>We use the collected information for various purposes:</p>
              <ul>
                <li>To create and maintain your account</li>
                <li>To provide, operate, and maintain our services</li>
                <li>To generate PDFs from your form submissions</li>
                <li>To process your payments and manage your subscription</li>
                <li>To send you technical notices, updates, security alerts, and support messages</li>
                <li>To respond to your comments, questions, and customer service requests</li>
                <li>To monitor and analyze usage patterns and trends</li>
                <li>To detect, prevent, and address technical issues</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2>4. Data Storage and Security</h2>
              <p><strong>Important Note About Our Service:</strong> {config.APP_NAME} is designed as a "fill and print" platform. We do not permanently store your form responses or submitted data. Once you generate a PDF, we encourage you to download and save it locally. We do not retain your form data on our servers.</p>
              
              <h3>4.1 How We Protect Your Information</h3>
              <ul>
                <li>We use industry-standard encryption (SSL/TLS) to protect data transmitted between your browser and our servers</li>
                <li>We implement appropriate technical and organizational security measures</li>
                <li>We regularly update our security practices and software</li>
                <li>We limit access to personal information to authorized personnel only</li>
              </ul>

              <h3>4.2 Data Retention</h3>
              <p>We retain your account information for as long as your account is active. Form responses are not stored by default - they exist only during the current session. You are responsible for downloading and saving your PDFs locally.</p>
            </section>

            <section>
              <h2>5. Sharing Your Information</h2>
              <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> We may share information with third-party vendors who provide services on our behalf (payment processing, hosting, email delivery).</li>
                <li><strong>Legal Compliance:</strong> We may disclose information if required by law or in response to valid legal requests.</li>
                <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of assets, your information may be transferred.</li>
              </ul>
            </section>

            <section>
              <h2>6. Your Rights and Choices</h2>
              <p>Depending on your location, you may have certain rights regarding your personal information:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              </ul>
              <p>To exercise these rights, please contact us at <a href={`mailto:${config.PRIVACY_EMAIL}`}>{config.PRIVACY_EMAIL}</a>.</p>
            </section>

            <section>
              <h2>7. Third-Party Services</h2>
              <p>Our platform integrates with third-party services for specific functionality:</p>
              <ul>
                <li><strong>AWS S3:</strong> For temporary image storage during form submission and PDF generation</li>
                <li><strong>Payment Processors:</strong> For secure payment processing</li>
                <li><strong>Email Services:</strong> For sending notifications and support communications</li>
              </ul>
              <p>These third parties have their own privacy policies, and we encourage you to review them.</p>
            </section>

            <section>
              <h2>8. Children's Privacy</h2>
              <p>Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.</p>
            </section>

            <section>
              <h2>9. International Data Transfers</h2>
              <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from those in your country.</p>
            </section>

            <section>
              <h2>10. Changes to This Privacy Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.</p>
            </section>

            <section>
              <h2>11. Contact Us</h2>
              <p>If you have any questions about this Privacy Policy, please contact us:</p>
              <ul>
                <li>Email: <a href={`mailto:${config.PRIVACY_EMAIL}`}>{config.PRIVACY_EMAIL}</a></li>
                <li>Support: <a href={`mailto:${config.SUPPORT_EMAIL}`}>{config.SUPPORT_EMAIL}</a></li>
                <li>Address: 123 Business Avenue, Suite 100, San Francisco, CA 94105</li>
              </ul>
            </section>

            <section className="consent-section">
              <h2>12. Your Consent</h2>
              <p>By using our platform, you consent to this Privacy Policy and agree to its terms.</p>
            </section>
          </div>
        </div>
      </div>

      <CommonFooter onNavigate={onNavigate} activePage="privacy" />
    </div>
  );
};

export default PrivacyPolicy;